import { UniformsUtils, Vector3 } from 'three';
import { getWrappedObject } from '../constructionTools';
import { getContainerBox } from '../utils/objectSize';

function applyCutShader(shaderObject, params = {}) {
  const boxes = params.boxes?.length
    ? params.boxes
    : [
        {
          min: new Vector3(),
          max: new Vector3(),
        },
      ];
  if (!params.boxes && params.boxCount) {
    boxes.length = 0;
    for (let i = 0; i < params.boxCount; i++) {
      boxes.push({
        min: new Vector3(),
        max: new Vector3(),
      });
    }
  }

  if (!shaderObject.uniforms.clippingBoxes) {
    shaderObject.uniforms = UniformsUtils.merge([
      shaderObject.uniforms,
      {
        clippingBoxes: {
          value: boxes,
        },
      },
    ]);
    shaderObject.vertexShaderOrig = shaderObject.vertexShader;
    shaderObject.fragmentShaderOrig = shaderObject.fragmentShader;
  } else if (shaderObject.uniforms.clippingBoxes.value !== boxes) {
    shaderObject.uniforms.clippingBoxes.value = boxes;
  }

  Object.assign(shaderObject, {
    vertexShader: shaderObject.vertexShaderOrig
      .replace('#include <common>', 'varying vec3 vModelPosition;\n#include <common>')
      .replace(
        '#include <begin_vertex>',
        'vModelPosition = (modelMatrix * vec4(position, 1.0)).xyz;\n#include <begin_vertex>'
      ),

    fragmentShader: shaderObject.fragmentShaderOrig
      .replace(
        'uniform float opacity;',
        'uniform float opacity;\n' +
          'struct ClippingBox { \n' +
          '    vec3 min;\n' +
          '    vec3 max;\n' +
          '};\n' +
          'uniform ClippingBox clippingBoxes[' +
          boxes.length +
          '];\n' +
          'bool isClippedByBox(vec3 lo, vec3 hi, vec3 p) {\n' +
          '    return all(greaterThan(p, lo)) && all(lessThan(p, hi));\n' +
          '}\n'
      )
      .replace('void main() {', 'varying vec3 vModelPosition;\nvoid main() {')
      .replace(
        '#include <clipping_planes_fragment>',
        '#include <clipping_planes_fragment>\n' +
          boxes
            .map(
              (_, idx) =>
                `    if (isClippedByBox(clippingBoxes[${idx}].min, clippingBoxes[${idx}].max, vModelPosition)) discard;`
            )
            .join('\n')
      ),
  });

  shaderObject.clippingBoxesLength = boxes.length;
}

function findClosestWall(object) {
  if (!object) {
    return;
  }
  const wo = getWrappedObject(object);
  return wo.userData?.type === 'wall' ? object : findClosestWall(object.parent);
}

const clippingBoxes = {
  front: [],
  back: [],
  left: [],
  right: [],
};

export function clearClippingBoxes() {
  clippingBoxes.front.length = 0;
  clippingBoxes.back.length = 0;
  clippingBoxes.left.length = 0;
  clippingBoxes.right.length = 0;
}

function getWallBoxes(wall) {
  return clippingBoxes[wall.userData.side];
}

function updateWallBox(object, root) {
  root = root || findClosestWall(object) || object.wall;
  const box = getContainerBox(object).expandByScalar(-0.001);
  const models = [];
  root.traverse((obj) => {
    if (obj.userData?.hasClippingBox) {
      const objBox = getContainerBox(obj);
      if (objBox.intersectsBox(box)) {
        objBox.expandByScalar(0.1);
        const objSize = objBox.getSize(new Vector3());
        if (objSize.x < objSize.z) {
          // front and rear
          box.min.x = objBox.min.x;
          box.max.x = objBox.max.x;
        } else {
          box.min.z = objBox.min.z;
          box.max.z = objBox.max.z;
        }
        const wallBoxes = getWallBoxes(obj);
        const cb = wallBoxes.find(({ objectId }) => objectId === object.uuid);
        if (cb) {
          cb.copy(box);
        } else {
          box.objectId = object.uuid;
          wallBoxes.push(box);
        }

        models.push(obj);
      }
    }
  });

  for (const model of models) {
    const boxes = getWallBoxes(model);
    model.traverse((obj) => {
      if (obj.material) {
        if (!obj.material.isClippingBoxMaterial) {
          obj.material.onBeforeCompile = function (shaderObject) {
            applyCutShader(shaderObject, { boxes });
            obj.material.userData.shader = shaderObject;
          };
          obj.material.customProgramCacheKey = function () {
            return boxes?.length || 0;
          };
          obj.material.isClippingBoxMaterial = true;
        }
      }
    });
  }
}

export function addClippingBoxForObject(object) {
  const updateMatrixWorldOrig = object.updateMatrixWorld;
  object.updateMatrixWorld = function (force) {
    updateMatrixWorldOrig.call(this, force);
    const mxwStr = this.matrixWorld.elements.toString();
    if (mxwStr === this._prevMxwStr) {
      return;
    }
    this._prevMxwStr = mxwStr;
    updateWallBox(this);
  };
}
